import { ArrowPathIcon } from '@heroicons/react/20/solid';
import Tag from 'components/common/tag';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger
} from 'components/layout/popover';
import Tooltip from 'components/layout/tooltip';
import ProductPhoto from 'components/products/product-photo';
import { AirHandlerProductType, CondenserProductType } from 'data/types';
import { useState } from 'react';
import {
  DuctedSwapOption,
  DuctedSwapSlot
} from 'utils/ducted-component-swap-types';
import { abbreviateNumber } from 'utils/helpers';
import { ptacElectricalLabel } from 'utils/ptac-electrical-label';
import { PTAC_UNIT_SWAP_EMPTY_MESSAGE } from 'utils/ptac-unit-swap';
import DuctedSwapRows from './ducted-swap-rows';
import { NumericDeltaTag, PriceDelta, SwapRowCell } from './swap-row-cells';

type SwapProductPopoverBaseProps = Readonly<{
  hasActiveLocks: boolean;
  tooltipText: string;
  isLoading?: boolean;
}>;

type CondenserSwapPopoverProps = SwapProductPopoverBaseProps &
  Readonly<{
    variant: 'condenser';
    products: CondenserProductType[];
    currentProduct: CondenserProductType;
    onSelect: (productId: number) => void;
  }>;

type AirHandlerSwapPopoverProps = SwapProductPopoverBaseProps &
  Readonly<{
    variant: 'airHandler';
    products: AirHandlerProductType[];
    currentProduct: AirHandlerProductType;
    onSelect: (productId: number) => void;
  }>;

// Hotel Style PTAC: the unit is the whole system, so the swap offers the
// other sizes that fit the room and shows each one's electrical rating.
type PtacUnitSwapPopoverProps = SwapProductPopoverBaseProps &
  Readonly<{
    variant: 'ptacUnit';
    products: CondenserProductType[];
    currentProduct: CondenserProductType;
    onSelect: (productId: number) => void;
  }>;

type DuctedSwapPopoverProps = SwapProductPopoverBaseProps &
  Readonly<{
    variant: 'ducted';
    slot: DuctedSwapSlot;
    products: DuctedSwapOption[];
    currentPrice: number;
    onSelect: (modelKey: string) => void;
  }>;

type SwapProductPopoverProps =
  | CondenserSwapPopoverProps
  | AirHandlerSwapPopoverProps
  | PtacUnitSwapPopoverProps
  | DuctedSwapPopoverProps;

const LOADING_MESSAGE = 'Loading systems...';

const DUCTED_SLOT_PLURALS: Record<DuctedSwapSlot, string> = {
  outdoor: 'outdoor units',
  indoor: 'indoor units',
  blower: 'furnaces or blowers'
};

const formatBtu = (value: number): string => abbreviateNumber(value);
const formatSeer = (value: number): string =>
  String(Math.round(value * 10) / 10);

const matchesQuery = (
  product: CondenserProductType | AirHandlerProductType,
  query: string
): boolean =>
  query === '' ||
  product.model.toLowerCase().includes(query) ||
  (product.name ?? '').toLowerCase().includes(query) ||
  ('series' in product && (product.series ?? '').toLowerCase().includes(query));

const ductedOptionMatchesQuery = (
  option: DuctedSwapOption,
  query: string
): boolean =>
  query === '' ||
  option.modelNumber.toLowerCase().includes(query) ||
  option.manufacturer.toLowerCase().includes(query) ||
  (option.product?.name ?? '').toLowerCase().includes(query);

const emptyMessageFor = (props: SwapProductPopoverProps): string => {
  if (props.variant === 'condenser') return 'No other condensers available.';
  if (props.variant === 'airHandler') {
    return 'No other air handlers available.';
  }
  if (props.variant === 'ptacUnit') return PTAC_UNIT_SWAP_EMPTY_MESSAGE;
  return `No other ${
    DUCTED_SLOT_PLURALS[props.slot]
  } available that match your current filters.`;
};

const unlockHintFor = (props: SwapProductPopoverProps): string | null => {
  if (props.variant === 'condenser') {
    return ' Try unlocking zones to see more options.';
  }
  if (props.variant === 'airHandler') {
    return ' Try unlocking condenser or other zones to see more options.';
  }
  if (props.variant === 'ptacUnit') return null;
  return ' Try unlocking other components to see more options.';
};

function CondenserSwapRows({
  products,
  currentProduct,
  onSelect,
  showElectrical = false
}: Readonly<{
  products: CondenserProductType[];
  currentProduct: CondenserProductType;
  onSelect: (productId: number) => void;
  showElectrical?: boolean;
}>) {
  return (
    <>
      {products
        .toSorted((a, b) => a.seer - b.seer)
        .toSorted((a, b) => a.coolingBTU - b.coolingBTU)
        .map((product) => (
          <PopoverClose
            key={product.productId}
            className='group table-row rounded-md px-2 py-1 font-semibold'
            onClick={() => onSelect(product.productId)}
          >
            <SwapRowCell first>
              <ProductPhoto product={product} size='small' />
            </SwapRowCell>
            <SwapRowCell>
              <span className='whitespace-nowrap'>{product.model}</span>
            </SwapRowCell>
            <SwapRowCell>
              <NumericDeltaTag
                value={product.coolingBTU}
                currentValue={currentProduct.coolingBTU}
                unit='BTU'
                format={formatBtu}
              />
            </SwapRowCell>
            <SwapRowCell>
              <NumericDeltaTag
                value={product.seer}
                currentValue={currentProduct.seer}
                unit='SEER'
                format={formatSeer}
              />
            </SwapRowCell>
            {showElectrical && (
              <SwapRowCell>
                <Tag noWrap>{ptacElectricalLabel(product)}</Tag>
              </SwapRowCell>
            )}
            <SwapRowCell last>
              <PriceDelta
                price={product.price}
                currentPrice={currentProduct.price}
              />
            </SwapRowCell>
          </PopoverClose>
        ))}
    </>
  );
}

function AirHandlerSwapRows({
  products,
  currentProduct,
  onSelect
}: Readonly<{
  products: AirHandlerProductType[];
  currentProduct: AirHandlerProductType;
  onSelect: (productId: number) => void;
}>) {
  return (
    <>
      {products
        .toSorted((a, b) => a.coolingBTU - b.coolingBTU)
        .toSorted((a, b) => a.type.localeCompare(b.type))
        .map((product) => (
          <PopoverClose
            key={product.productId}
            className='group table-row rounded-md px-2 py-1 font-semibold'
            onClick={() => onSelect(product.productId)}
          >
            <SwapRowCell first>
              <ProductPhoto product={product} size='small' />
            </SwapRowCell>
            <SwapRowCell>
              <span className='whitespace-nowrap'>{product.model}</span>
            </SwapRowCell>
            <SwapRowCell>
              <Tag noWrap text={product.type} />
            </SwapRowCell>
            <SwapRowCell>
              <NumericDeltaTag
                value={product.coolingBTU}
                currentValue={currentProduct.coolingBTU}
                unit='BTU'
                format={formatBtu}
              />
            </SwapRowCell>
            <SwapRowCell last>
              <PriceDelta
                price={product.price}
                currentPrice={currentProduct.price}
              />
            </SwapRowCell>
          </PopoverClose>
        ))}
    </>
  );
}

function SwapRows({
  popover,
  query
}: Readonly<{ popover: SwapProductPopoverProps; query: string }>) {
  if (popover.variant === 'condenser' || popover.variant === 'ptacUnit') {
    return (
      <CondenserSwapRows
        products={popover.products.filter((product) =>
          matchesQuery(product, query)
        )}
        currentProduct={popover.currentProduct}
        onSelect={popover.onSelect}
        showElectrical={popover.variant === 'ptacUnit'}
      />
    );
  }
  if (popover.variant === 'airHandler') {
    return (
      <AirHandlerSwapRows
        products={popover.products.filter((product) =>
          matchesQuery(product, query)
        )}
        currentProduct={popover.currentProduct}
        onSelect={popover.onSelect}
      />
    );
  }
  return (
    <DuctedSwapRows
      options={popover.products.filter((option) =>
        ductedOptionMatchesQuery(option, query)
      )}
      currentPrice={popover.currentPrice}
      onSelect={popover.onSelect}
    />
  );
}

const countResults = (
  props: SwapProductPopoverProps,
  query: string
): number => {
  if (props.variant === 'ducted') {
    return props.products.filter((option) =>
      ductedOptionMatchesQuery(option, query)
    ).length;
  }
  return props.products.filter((product) => matchesQuery(product, query))
    .length;
};

export default function SwapProductPopover(props: SwapProductPopoverProps) {
  const { hasActiveLocks, tooltipText, isLoading = false } = props;
  const [search, setSearch] = useState('');
  const query = search.toLowerCase();
  const hasResults = !isLoading && countResults(props, query) > 0;

  return (
    <Popover placement='bottom-start'>
      <PopoverTrigger
        aria-label={tooltipText}
        className='relative text-gray-400 hover:text-gray-500'
      >
        <ArrowPathIcon className='h-5 w-5' />
        <Tooltip text={tooltipText} />
      </PopoverTrigger>
      <PopoverContent className='z-50 max-h-[500px] overflow-auto rounded-lg bg-white shadow-lg ring-1 ring-black/5'>
        <div className='flex flex-col'>
          <div className='sticky top-0 z-10 bg-white p-2'>
            <input
              type='text'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder='Search model or name'
              className='w-full rounded-md border border-gray-300 p-2 text-sm'
            />
          </div>
          {hasResults && (
            <div className='mx-2 mb-2 table'>
              <div className='table-row-group'>
                <SwapRows popover={props} query={query} />
              </div>
            </div>
          )}
          {!hasResults && isLoading && (
            <div className='max-w-[400px] px-3 pb-3 text-gray-500'>
              {LOADING_MESSAGE}
            </div>
          )}
          {!hasResults && !isLoading && (
            <div className='max-w-[400px] px-3 pb-3'>
              {emptyMessageFor(props)}
              {hasActiveLocks && unlockHintFor(props)}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
